/**
 * Financeiro › Obras (HU 2670) — contrato de `GET /api/financeiro/obras` (privilégio
 * `hub.financeiro:view-painel`, verificado pelo core). Condição, Realizado/Outlook e os percentuais RE/EM/CO/NC/ES
 * são campos opcionais do contrato: o core ainda não os envia, então o gateway os normaliza para `null`
 * (a tela mostra "-") e eles passam a aparecer sem mudança no front quando o core os enviar.
 */

/** Status financeiro (enum do core), na ordem das colunas dos Cards. */
export interface FinanceiroStatusOption {
  codigo: string;
  /** Nome completo (tooltip da tag, filtro e coluna dos Cards). */
  descricao: string;
  /** Rótulo abreviado da tag na Lista (Figma 12760:965620: FECH, EXEC, FEL-3). */
  abreviacao: string;
}

export const FINANCEIRO_STATUS_OPTIONS: readonly FinanceiroStatusOption[] = [
  { codigo: "FEL_0", descricao: "FEL-0", abreviacao: "FEL-0" },
  { codigo: "FEL_1", descricao: "FEL-1", abreviacao: "FEL-1" },
  { codigo: "FEL_2", descricao: "FEL-2", abreviacao: "FEL-2" },
  { codigo: "FEL_3", descricao: "FEL-3", abreviacao: "FEL-3" },
  { codigo: "FEL_4", descricao: "FEL-4", abreviacao: "FEL-4" },
  { codigo: "EXECUCAO", descricao: "Execução", abreviacao: "EXEC" },
  { codigo: "FECHAMENTO", descricao: "Fechamento", abreviacao: "FECH" },
  // Abreviações de Pós-Audit e Concluída não aparecem no Figma: pendência de confirmação.
  { codigo: "POS_AUDIT", descricao: "Pós-Audit", abreviacao: "PÓS" },
  { codigo: "CONCLUIDA", descricao: "Concluída", abreviacao: "CONC" },
];

export function findFinanceiroStatus(codigo: string): FinanceiroStatusOption | undefined {
  return FINANCEIRO_STATUS_OPTIONS.find((option) => option.codigo === codigo);
}

export interface FinanceiroTipoObra {
  codigo: string;
  nome: string;
}

/** Opção do filtro Embarcação (`GET /api/financeiro/obras/embarcacoes`). */
export interface FinanceiroEmbarcacaoOpcao {
  id: string;
  nome: string;
}

/** Opção do filtro Tipo (`GET /api/financeiro/obras/tipos-obra`). */
export interface FinanceiroTipoObraOpcao {
  id: string;
  codigo: string;
  nome: string;
}

/** Estado de uma consulta de opções de filtro. */
export interface FinanceiroLookupState<T> {
  status: "loading" | "success" | "error" | "forbidden";
  data: T;
  errorMessage: string | null;
}

export interface FinanceiroObraItem {
  id: string;
  codigo: string;
  embarcacao: { id: string; nome: string; acronimo?: string | null };
  statusFinanceiro: { codigo: string; descricao: string } | null;
  tiposObra: FinanceiroTipoObra[];
  /** `yyyy-MM-dd`. */
  inicio: string;
  /** `yyyy-MM-dd`. */
  fim: string;
  duracaoDias: number | null;
  /** Condição da parada em texto livre (ex.: "Seco"); `null` = o core não enviou. */
  condicao: string | null;
  /** Realizado em milhões de reais (BRL M); `null` = ausente. */
  realizadoBrlMilhoes: number | null;
  /** Outlook em milhões de reais (BRL M); `null` = ausente. */
  outlookBrlMilhoes: number | null;
  /** RE = Realizado (%): financeiro com nota fiscal classificada pela contabilidade. */
  realizadoPercentual: number | null;
  /** EM = Empenhado (%): financeiro com pedido de compras aprovado. */
  empenhadoPercentual: number | null;
  /**
   * CO = Cotado (%): financeiro com cotação atualizada de fornecedor/compras. O nome do campo
   * (`comprometido…`) é herdado do módulo Obras; o significado é "Cotado" (dicionário oficial da HU 2670).
   */
  comprometidoPercentual: number | null;
  /** NC = Não Comprometido (%): financeiro de escopo macro sem associação a serviços, materiais ou facilidades. */
  naoComprometidoPercentual: number | null;
  /** ES = Estimado (%): financeiro estimado com base em premissas e histórico. */
  estimadoPercentual: number | null;
}

export interface FinanceiroObraTotalPorStatus {
  /** Código do status; `null` = obras sem status financeiro. */
  statusFinanceiro: string | null;
  total: number;
}

export interface FinanceiroObrasPage {
  items: FinanceiroObraItem[];
  total: number;
  totaisPorStatus: FinanceiroObraTotalPorStatus[];
}

/** Critérios da consulta; `statusFinanceiro`, `tipoObra` e `embarcacaoId` aceitam vários valores. */
export interface FinanceiroObrasFilters {
  /** Códigos do enum de status financeiro. */
  statusFinanceiro: string[];
  embarcacaoId: string[];
  /** Códigos do tipo de obra. */
  tipoObra: string[];
  /** Condição da obra: `SECO` ou `MOLHADO`. */
  condicao: string[];
  /** `yyyy-MM-dd` ou vazio. */
  dataInicio: string;
  /** `yyyy-MM-dd` ou vazio. */
  dataFim: string;
}

export const EMPTY_FINANCEIRO_OBRAS_FILTERS: FinanceiroObrasFilters = {
  statusFinanceiro: [],
  embarcacaoId: [],
  tipoObra: [],
  condicao: [],
  dataInicio: "",
  dataFim: "",
};

export function hasFinanceiroObrasFilters(filters: FinanceiroObrasFilters): boolean {
  return (
    filters.statusFinanceiro.length > 0 ||
    filters.embarcacaoId.length > 0 ||
    filters.tipoObra.length > 0 ||
    filters.condicao.length > 0 ||
    filters.dataInicio !== "" ||
    filters.dataFim !== ""
  );
}

/** Campos ordenáveis do contrato; as demais colunas não ordenam. */
export const FINANCEIRO_OBRAS_SORT_FIELDS = [
  "id",
  "embarcacao",
  "statusFinanceiro",
  "inicio",
  "termino",
] as const;

export type FinanceiroObrasSortField = (typeof FINANCEIRO_OBRAS_SORT_FIELDS)[number];

export function isFinanceiroObrasSortField(value: unknown): value is FinanceiroObrasSortField {
  return (FINANCEIRO_OBRAS_SORT_FIELDS as readonly unknown[]).includes(value);
}

export interface FinanceiroObrasSort {
  field: FinanceiroObrasSortField;
  direction: "asc" | "desc";
}

/** Ordem inicial: ID crescente, como nos dados do protótipo (a validar com a HU). */
export const DEFAULT_FINANCEIRO_OBRAS_SORT: FinanceiroObrasSort = {
  field: "id",
  direction: "asc",
};

/** Máximo aceito pelo bff por página. */
export const MAX_FINANCEIRO_OBRAS_PAGE_SIZE = 100;

/** Colunas da tela de Financeiro > Obras, na ordem da tabela (a exportação traduz cada uma para a chave do core). */
export type FinanceiroObrasColumnKey =
  | "id"
  | "embarcacao"
  | "statusFinanceiro"
  | "tipoObra"
  | "realizado"
  | "outlook"
  | "re"
  | "em"
  | "co"
  | "es"
  | "nc"
  | "condicao"
  | "inicio"
  | "termino"
  | "duracao"
  | "detalhamento";
